import React, { useState, useEffect } from 'react';
import { Flag, Trophy, Clock, HelpCircle, X, CheckCircle, Lock } from 'lucide-react';
import '../styles/ctf.css';

const CTFPanel = ({ onClose, language }) => {
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [flagInput, setFlagInput] = useState('');
  const [solvedChallenges, setSolvedChallenges] = useState([]);
  const [score, setScore] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [message, setMessage] = useState('');
  const [hintsUsed, setHintsUsed] = useState([]);

  // Cargar progreso guardado
  useEffect(() => {
    const savedProgress = localStorage.getItem('ctf_progress');
    if (savedProgress) {
      const progress = JSON.parse(savedProgress);
      setSolvedChallenges(progress.solved || []);
      setScore(progress.score || 0);
      setStartTime(progress.startTime || Date.now());
      setHintsUsed(progress.hintsUsed || []);
    } else {
      setStartTime(Date.now());
    }
    
    // Plantar flag en localStorage para el reto 5
    localStorage.setItem('ctf_secret', 'ANDR31{l0c4l_st0r4g3}');
  }, []);

  // Timer — only ticks while the panel is mounted/visible AND not all challenges are solved.
  // Use document.visibilityState so it pauses when tab is hidden as well.
  useEffect(() => {
    if (!startTime) return undefined;
    const totalChallenges = 8; // matches challenges.length below
    const allSolved = solvedChallenges.length >= totalChallenges;
    if (allSolved) return undefined;

    let lastTick = Date.now();
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') {
        // Don't accumulate while tab is hidden
        lastTick = Date.now();
        return;
      }
      const now = Date.now();
      const delta = Math.floor((now - lastTick) / 1000);
      if (delta > 0) {
        setElapsedTime((t) => t + delta);
        lastTick = now;
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, solvedChallenges.length]);

  // Guardar progreso (incluye elapsedTime para que persista al cerrar)
  useEffect(() => {
    if (startTime) {
      localStorage.setItem('ctf_progress', JSON.stringify({
        solved: solvedChallenges,
        score: score,
        startTime: startTime,
        elapsedTime: elapsedTime,
        hintsUsed: hintsUsed
      }));
    }
  }, [solvedChallenges, score, startTime, elapsedTime, hintsUsed]);

  const challenges = [
    {
      id: 1,
      category: 'Crypto',
      title: language === 'es' ? 'Mensaje Codificado' : 'Encoded Message',
      description: language === 'es' 
        ? 'Encontré este mensaje extraño en los logs: "QU5EUjMxe2I0czM2NF9kM2MwZDFuZ30="'
        : 'Found this weird message in the logs: "QU5EUjMxe2I0czM2NF9kM2MwZDFuZ30="',
      hint: 'Base64 encoding. Try online decoder or btoa/atob in console.',
      points: 100,
      flagHash: 'b02c24a9fb1925c48cf5eac4885d83294ff39a1322a7f75f03224b2fb06b69d9'
    },
    {
      id: 2,
      category: 'Crypto',
      title: language === 'es' ? 'Cifrado Clásico' : 'Classic Cipher',
      description: language === 'es'
        ? 'ROT13 es un cifrado clásico. Descifra: "NAQE31{sec_headers}"'
        : 'ROT13 is a classic cipher. Decrypt: "NAQE31{sec_headers}"',
      hint: 'ROT13: cada letra se rota 13 posiciones. A→N, N→A. Usa un decodificador online o hazlo manual.',
      points: 100,
      flagHash: '5b510b049051ab90c920ecd8a10ca65241a4ac1a2039aec68c2ac4ce1262fb2e'
    },
    {
      id: 3,
      category: 'OSINT',
      title: language === 'es' ? 'Identidad Digital' : 'Digital Identity',
      description: language === 'es'
        ? 'El propietario tiene un GitHub. Su username contiene parte de la flag.'
        : 'The owner has a GitHub. His username contains part of the flag.',
      hint: 'Check whoami command output. GitHub username is C4sh3R. Flag format: ANDR31{github_C4sh3R}',
      points: 100,
      flagHash: '9ebe159822efb0ab3424169effec5869bb614dc3563e02e5f2e94d3407196a64'
    },
    {
      id: 4,
      category: 'Reversing',
      title: language === 'es' ? 'Código Ofuscado' : 'Obfuscated Code',
      description: language === 'es'
        ? 'Ejecuta en la consola: atob("QU5EUjMxe3IzdjNyczFuZ18xc19mdW4hIX0=")' 
        : 'Run in console: atob("QU5EUjMxe3IzdjNyczFuZ18xc19mdW4hIX0=")',
      hint: 'Decode Base64, directly gives you the flag. Open browser console (F12) and run the command.',
      points: 150,
      flagHash: 'e670249d3eebdad8733a2ecc9277c2ecf7257b2ab5c41562ac84820541809707'
    },
    {
      id: 5,
      category: 'Forensics',
      title: language === 'es' ? 'Almacenamiento Local' : 'Local Storage',
      description: language === 'es'
        ? 'Los datos persisten en el navegador. Inspecciona localStorage en DevTools.'
        : 'Data persists in browser. Inspect localStorage in DevTools.',
      hint: 'Open DevTools → Application → Local Storage. Look for key "ctf_secret".',
      points: 150,
      flagHash: '80930ae566d9fc657f4242cd15f1e0665799a98c4216446fed2074afbc2c0d78'
    },
    {
      id: 6,
      category: 'Logic',
      title: language === 'es' ? 'Secuencia Fibonacci' : 'Fibonacci Sequence',
      description: language === 'es'
        ? 'El 15º número de Fibonacci en hexadecimal es parte de la flag: ANDR31{fib_HEX}'
        : '15th Fibonacci number in hexadecimal is part of flag: ANDR31{fib_HEX}',
      hint: 'Fib(15) = 610. Convert to hex = 0x262. Flag: ANDR31{fib_262}',
      points: 200,
      flagHash: '2865ec92cfe591dff8a353fb094c4d1db9900250d13b2da95d180cfbd794e1a4'
    },
    {
      id: 7,
      category: 'Web3',
      title: language === 'es' ? 'Smart Contract Vuln' : 'Smart Contract Vuln',
      description: language === 'es'
        ? 'Ejecuta "web3 scan" varias veces. Cuando encuentres Reentrancy Attack, la flag está oculta en el código vulnerable.'
        : 'Execute "web3 scan" multiple times. When you find Reentrancy Attack, flag is hidden in vulnerable code.',
      hint: 'In the Reentrancy code, look for a comment with the flag format. Flag: ANDR31{r3_3ntr4ncy}',
      points: 200,
      flagHash: 'bff9368bb170f1b68ad1682919a11dc25309c5ab68ba584b620477996e471d72'
    },
    {
      id: 8,
      category: 'Steganography',
      title: language === 'es' ? 'Icono Oculto' : 'Hidden Icon',
      description: language === 'es'
        ? 'El favicon SVG tiene un secreto. Visita /favicon.svg y busca comentarios ocultos.'
        : 'The favicon SVG has a secret. Visit /favicon.svg and look for hidden comments.',
      hint: 'Go to http://localhost:3000/favicon.svg in browser and view source. Look for XML/HTML comment.',
      points: 250,
      flagHash: 'a77b1ac1a76d380ad51270ba50d15219f9572a606336ad3829bdc3bf24724e66'
    }
  ];

  const hashFlag = async (flag) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(flag.trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleSubmitFlag = async () => {
    if (!currentChallenge || !flagInput) return;

    const hash = await hashFlag(flagInput);
    
    if (hash === currentChallenge.flagHash) {
      if (!solvedChallenges.includes(currentChallenge.id)) {
        const pointsEarned = hintsUsed.includes(currentChallenge.id) 
          ? Math.floor(currentChallenge.points * 0.7) 
          : currentChallenge.points;
        
        setSolvedChallenges([...solvedChallenges, currentChallenge.id]);
        setScore(score + pointsEarned);
        setMessage(`🎉 ¡Correcto! +${pointsEarned} puntos`);
        setFlagInput('');
        
        setTimeout(() => {
          setCurrentChallenge(null);
          setMessage('');
        }, 2000);
      } else {
        setMessage('⚠️ Ya resolviste este reto');
      }
    } else {
      setMessage('❌ Flag incorrecta. Intenta de nuevo.');
    }
    
    setTimeout(() => setMessage(''), 3000);
  };

  const handleUseHint = (challengeId) => {
    if (!hintsUsed.includes(challengeId)) {
      setHintsUsed([...hintsUsed, challengeId]);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const resetCTF = () => {
    const confirmed = window.confirm(language === 'es' ? '¿Reiniciar todo el progreso?' : 'Reset all progress?');
    if (!confirmed) return;

    // Limpiar localStorage relacionado con CTF
    localStorage.removeItem('ctf_progress');
    localStorage.removeItem('ctf_secret');

    // Resetear estados
    setSolvedChallenges([]);
    setScore(0);
    setStartTime(Date.now());
    setElapsedTime(0);
    setHintsUsed([]);
    setCurrentChallenge(null);
    setMessage('');
    setFlagInput('');

    // Replantar la flag de localStorage
    localStorage.setItem('ctf_secret', 'ANDR31{l0c4l_st0r4g3}');

    setTimeout(() => {
      alert(language === 'es' ? '✅ CTF reiniciado correctamente!' : '✅ CTF reset successfully!');
    }, 100);
  };

  if (currentChallenge) {
    const isSolved = solvedChallenges.includes(currentChallenge.id);
    const hintUsed = hintsUsed.includes(currentChallenge.id);

    return (
      <div className="ctf-overlay">
        <div className="ctf-panel ctf-challenge-panel">
          <div className="ctf-header">
            <div className="ctf-header-left">
              <Flag size={24} color="#b06bff" />
              <h2>{currentChallenge.category} - {currentChallenge.title}</h2>
            </div>
            <button onClick={() => setCurrentChallenge(null)} className="ctf-close-btn">
              <X size={20} />
            </button>
          </div>

          <div className="ctf-content">
            <div className="challenge-badge">
              <span className="challenge-category">{currentChallenge.category}</span>
              <span className="challenge-points">{currentChallenge.points} pts</span>
            </div>

            <div className="challenge-description">
              <p>{currentChallenge.description}</p>
            </div>

            {!hintUsed && !isSolved && (
              <button 
                className="ctf-hint-btn"
                onClick={() => handleUseHint(currentChallenge.id)}
              >
                <HelpCircle size={16} />
                {language === 'es' ? 'Ver pista (-30% puntos)' : 'Show hint (-30% points)'}
              </button>
            )}

            {hintUsed && (
              <div className="challenge-hint">
                <HelpCircle size={16} color="#ffd700" />
                <p>{currentChallenge.hint}</p>
              </div>
            )}

            {!isSolved ? (
              <div className="flag-input-container">
                <input
                  type="text"
                  className="flag-input"
                  placeholder="ANDR31{...}"
                  value={flagInput}
                  onChange={(e) => setFlagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSubmitFlag()}
                />
                <button onClick={handleSubmitFlag} className="flag-submit-btn">
                  {language === 'es' ? 'Enviar Flag' : 'Submit Flag'}
                </button>
              </div>
            ) : (
              <div className="challenge-solved">
                <CheckCircle size={32} color="#28ca42" />
                <p>{language === 'es' ? '¡Reto completado!' : 'Challenge solved!'}</p>
              </div>
            )}

            {message && (
              <div className={`challenge-message ${message.includes('Correcto') || message.includes('Correct') ? 'success' : 'error'}`}>
                {message}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ctf-overlay">
      <div className="ctf-panel">
        <div className="ctf-header">
          <div className="ctf-header-left">
            <Trophy size={24} color="#b06bff" />
            <h2>{language === 'es' ? '🚩 ANDR31 Mini CTF' : '🚩 ANDR31 Mini CTF'}</h2>
          </div>
          <button onClick={onClose} className="ctf-close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="ctf-scoreboard">
          <div className="score-item">
            <Trophy size={20} color="#ffd700" />
            <span>{language === 'es' ? 'Puntos' : 'Score'}: {score}</span>
          </div>
          <div className="score-item">
            <Flag size={20} color="#b06bff" />
            <span>{solvedChallenges.length}/8 {language === 'es' ? 'Resueltos' : 'Solved'}</span>
          </div>
          <div className="score-item">
            <Clock size={20} color="#ff5f57" />
            <span>{formatTime(elapsedTime)}</span>
          </div>
        </div>

        <div className="ctf-content">
          <div className="challenges-grid">
            {challenges.map((challenge) => {
              const isSolved = solvedChallenges.includes(challenge.id);
              return (
                <div 
                  key={challenge.id}
                  className={`challenge-card ${isSolved ? 'solved' : ''}`}
                  onClick={() => setCurrentChallenge(challenge)}
                >
                  <div className="challenge-card-header">
                    <span className="challenge-num">#{challenge.id}</span>
                    <span className="challenge-category-badge">{challenge.category}</span>
                    {isSolved && <CheckCircle size={20} color="#28ca42" />}
                    {!isSolved && <Lock size={20} color="#888" />}
                  </div>
                  <h3>{challenge.title}</h3>
                  <div className="challenge-card-footer">
                    <span className="challenge-points-badge">{challenge.points} pts</span>
                  </div>
                </div>
              );
            })}
          </div>

          {solvedChallenges.length === 8 && (
            <div className="ctf-complete">
              <Trophy size={48} color="#ffd700" />
              <h2>{language === 'es' ? '¡CTF COMPLETADO!' : 'CTF COMPLETED!'}</h2>
              <p>{language === 'es' ? 'Puntuación final' : 'Final score'}: {score}</p>
              <p>{language === 'es' ? 'Tiempo' : 'Time'}: {formatTime(elapsedTime)}</p>
            </div>
          )}

          <button onClick={resetCTF} className="ctf-reset-btn">
            {language === 'es' ? 'Reiniciar CTF' : 'Reset CTF'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CTFPanel;
